import { toOrganizationSearchRequestDto } from '../model/mappers/search-request.dto';
import { toSystemStatusModel } from '../model/mappers/system-status';

import type { Organization, OrganizationCreateDto } from '../model/types/organization.dto';
import type { OrganizationSearchRequestDto } from '../model/types/search-request.dto';
import type { OrganizationSearchRequestModel } from '../model/types/search-request.model';
import type { SystemStatus } from '../model/types/system-status.dto';
import type { SystemStatusModel } from '../model/types/system-status.model';
import type { PaginatedResponse } from '@/app/types/Response';
import type { Settings } from '@/app/types/Settings';
import type { ShiftActivityType } from '@/app/types/ShiftActivityType';
import type { User } from '@/app/types/User';
import type { SearchResponseDto } from '@/features/Shared/model/types/search-response.dto';
import type { SearchResponseModel } from '@/features/Shared/model/types/search-response.model';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { apiSlice } from '@/app/api/api';

export const organizationsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		createOrganization: builder.mutation<OrganizationCreateDto, Partial<OrganizationCreateDto>>(
			{
				query: (body) => {
					return {
						url: `/organizations/create`,
						method: 'POST',
						body,
					};
				},
				invalidatesTags: ['ORGANIZATIONS', 'SYSTEM_STATUS'],
			}
		),
		deleteOrganization: builder.mutation<{ id: string }, string>({
			query: (id) => {
				return {
					url: `/organizations/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: ['ORGANIZATIONS'],
		}),
		systemStatus: builder.infiniteQuery<
			SearchResponseModel<SystemStatusModel>,
			OrganizationSearchRequestModel,
			string | undefined
		>({
			infiniteQueryOptions: {
				initialPageParam: undefined,
				getNextPageParam: (lastPage: SearchResponseModel<SystemStatusModel>) => {
					return lastPage.moreResults ? (lastPage.continuation ?? undefined) : undefined;
				},
			},

			query: ({ queryArg, pageParam }) => ({
				url: `organizations/status/search`,
				method: 'POST',
				body: {
					...toOrganizationSearchRequestDto(queryArg),
					continuation_token: pageParam ?? undefined,
				} satisfies OrganizationSearchRequestDto,
			}),
			transformResponse: (response: SearchResponseDto<SystemStatus>) => {
				return {
					items: (response.items ?? []).map(toSystemStatusModel),
					continuation: response.meta.continuation,
					moreResults: response.meta.more_results,
				};
			},
		}),
		updateGeneralInfo: builder.mutation<Organization, Organization>({
			query: (body) => {
				return {
					url: `/organizations/${body.id}`,
					method: 'PUT',
					body,
				};
			},
			invalidatesTags: ['ORGANIZATIONS', 'SYSTEM_STATUS'],
		}),
		getOrganization: builder.query<Organization, string>({
			query: (id) => {
				return {
					url: `/organizations/${id}`,
					method: 'GET',
				};
			},
		}),
		getOrganizationSettings: builder.query<Settings, string>({
			query: (id) => {
				return {
					url: `/organizations/${id}/settings`,
					method: 'GET',
				};
			},
		}),
		updateOrganizationSettings: builder.mutation<Settings, { id: string; settings: Settings }>({
			query: ({ id, settings: body }) => {
				return {
					url: `/organizations/${id}/settings`,
					method: 'POST',
					body,
				};
			},
		}),
		organizationUsers: builder.query<User[], string>({
			query: (id) => {
				return {
					url: `/organizations/${id}/users/search`,
					method: 'POST',
					body: {
						page_size: 250,
					},
				};
			},
			transformResponse: (response: PaginatedResponse<User>) => response.items,
		}),
		organizationActivities: builder.query<ShiftActivityType[], string>({
			query: (id) => {
				return {
					url: `/organizations/${id}/shift-activity-types/search`,
					method: 'POST',
					body: {
						page_size: 250,
					},
				};
			},
			providesTags: (_result, _error, orgId) => [
				{
					type: 'ORGANIZATIONS_SHIFT_ACTIVITY_TYPES',
					id: orgId,
				},
			],
			transformResponse: (response: PaginatedResponse<ShiftActivityType>) => response.items,
		}),
		createShiftActivityType: builder.mutation<
			ShiftActivityType,
			{ orgId: string; shiftActivityType: Partial<ShiftActivityType> }
		>({
			query: ({ orgId, shiftActivityType }) => {
				return {
					url: `/organizations/${orgId}/shift-activity-types`,
					method: 'POST',
					body: shiftActivityType,
				};
			},
			invalidatesTags: (_result, _error, { orgId }) => [
				{
					type: 'ORGANIZATIONS_SHIFT_ACTIVITY_TYPES',
					id: orgId,
				},
			],
		}),
		updateShiftActivityType: builder.mutation<
			ShiftActivityType,
			{ orgId: string; shiftActivityType: Partial<ShiftActivityType> }
		>({
			query: ({ orgId, shiftActivityType }) => {
				return {
					url: `/organizations/${orgId}/shift-activity-types/${shiftActivityType.id}`,
					method: 'PUT',
					body: shiftActivityType,
				};
			},
			invalidatesTags: (_result, _error, { orgId }) => [
				{
					type: 'ORGANIZATIONS_SHIFT_ACTIVITY_TYPES',
					id: orgId,
				},
			],
		}),
		deleteShiftActivityType: builder.mutation<ShiftActivityType, { orgId: string; id: string }>(
			{
				query: ({ orgId, id }) => {
					return {
						url: `/organizations/${orgId}/shift-activity-types/${id}`,
						method: 'DELETE',
					};
				},
				invalidatesTags: (_result, _error, { orgId }) => [
					{
						type: 'ORGANIZATIONS_SHIFT_ACTIVITY_TYPES',
						id: orgId,
					},
				],
			}
		),
		defaultSettings: builder.query<Settings, void>({
			query: () => ({
				url: `organizations/defaults/settings`,
				method: 'GET',
			}),
		}),
		setPortalLogoBase64: builder.mutation<void, { base64: string; orgId: string }>({
			queryFn: async ({ base64, orgId }, _queryApi, _extraOptions, baseQuery) => {
				try {
					const response = await baseQuery({
						url: `organizations/${orgId}/settings/logo/portal`,
						method: 'POST',
						body: { data: base64 },
					});

					if (response.error) {
						throw new Error('Failed to upload the logo.');
					}

					return { data: undefined, error: undefined };
				} catch (error) {
					return { error: error as FetchBaseQueryError, data: undefined };
				}
			},
		}),
		setDeviceLogoBase64: builder.mutation<void, { base64: string; orgId: string }>({
			queryFn: async ({ base64, orgId }, _queryApi, _extraOptions, baseQuery) => {
				try {
					const response = await baseQuery({
						url: `organizations/${orgId}/settings/logo/devices`,
						method: 'POST',
						body: { data: base64 },
					});

					if (response.error) {
						throw new Error('Failed to upload the logo.');
					}

					return { data: undefined, error: undefined };
				} catch (error) {
					return { error: error as FetchBaseQueryError, data: undefined };
				}
			},
		}),
		portalLogo: builder.query<string, string>({
			query: (id) => ({
				url: `organizations/${id}/settings/logo/portal`,
				method: 'GET',
				responseHandler: 'text',
			}),
			transformResponse: (response: string) => {
				return `data:image/png;base64,${response}`;
			},
		}),
		deviceLogo: builder.query<string, string>({
			query: (id) => ({
				url: `/organizations/${id}/settings/logo/devices`,
				method: 'GET',
				responseHandler: 'text',
			}),
			transformResponse: (response: string) => {
				return `data:image/png;base64,${response}`;
			},
		}),
		checkName: builder.query<
			{
				is_available: true;
				available_hostname: string;
			},
			{ organization_name?: string }
		>({
			query: (body) => ({
				url: `organizations/check-name`,
				method: 'POST',
				body,
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useCheckNameQuery,
	useGetOrganizationQuery,
	useGetOrganizationSettingsQuery,
	useOrganizationUsersQuery,
	useOrganizationActivitiesQuery,
	useCreateOrganizationMutation,
	useLazyCheckNameQuery,
	useDefaultSettingsQuery,
	usePortalLogoQuery,
	useDeviceLogoQuery,
	useUpdateGeneralInfoMutation,
	useUpdateOrganizationSettingsMutation,
	useCreateShiftActivityTypeMutation,
	useUpdateShiftActivityTypeMutation,
	useDeleteShiftActivityTypeMutation,
	useSetPortalLogoBase64Mutation,
	useSetDeviceLogoBase64Mutation,
	useSystemStatusInfiniteQuery,
	useDeleteOrganizationMutation,
} = organizationsApiSlice;
